import { useState, useRef } from "react";
import { Upload, X, Image, Loader2, AlertCircle, Crown, Edit2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PhotoUpload({ venueId, ownerId, currentPhotos = [], captions = {}, isPremium = false, onPhotoChange, onCaptionChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionText, setCaptionText] = useState("");
  const fileInputRef = useRef(null);
  
  const maxPhotos = isPremium ? 5 : 2;
  const canUpload = currentPhotos.length < maxPhotos;

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    await uploadFile(files[0]);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    
    await uploadFile(files[0]);
  };

  const uploadFile = async (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    // Accept images up to 8MB, will be compressed before upload
    const MAX_UPLOAD_SIZE = 8 * 1024 * 1024;
    if (file.size > MAX_UPLOAD_SIZE) {
      toast.error(`Image must be less than 8MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`);
      return;
    }
    
    setUploading(true);
    
    try {
      // Compress large images before converting to base64
      let base64;
      if (file.size > 500 * 1024) { // Compress if > 500KB
        const compressed = await compressImage(file, 1600, 0.85);
        base64 = compressed.base64;
        
        // Check compressed size (max 2MB for storage)
        if (compressed.compressedSize > 2 * 1024 * 1024) {
          // Try more aggressive compression
          const recompressed = await compressImage(file, 1200, 0.7);
          base64 = recompressed.base64;
          
          if (recompressed.compressedSize > 2 * 1024 * 1024) {
            toast.error("Image could not be compressed to under 2MB. Please use a smaller image.");
            setUploading(false);
            return;
          }
        }
        
        const savedPercent = Math.round((1 - (base64.length * 3/4) / file.size) * 100);
        if (savedPercent > 20) {
          toast.info(`Image compressed by ${savedPercent}%`);
        }
      } else {
        base64 = await fileToBase64(file);
      }
      
      // Upload to server
      const response = await axios.post(`${API}/photos/upload?owner_id=${ownerId}`, {
        venue_id: venueId,
        image_data: base64,
        filename: file.name
      });
      
      if (response.data.success) {
        toast.success(`Photo uploaded (${response.data.total_photos}/${response.data.max_photos})`);
        if (onPhotoChange) {
          onPhotoChange(response.data.photo_url);
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes("Maximum")) {
        toast.error(detail);
      } else if (detail?.includes("too large")) {
        toast.error(detail);
      } else {
        toast.error("Failed to upload photo");
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Compress image helper
  const compressImage = (file, maxDimension, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }
          
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          const base64 = compressedDataUrl.split(',')[1];
          
          resolve({
            base64,
            preview: compressedDataUrl,
            compressedSize: Math.round((base64.length * 3) / 4)
          });
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDelete = async (index) => {
    if (!window.confirm("Delete this photo?")) return;
    
    try {
      await axios.delete(`${API}/photos/${venueId}/${index}?owner_id=${ownerId}`);
      toast.success("Photo deleted");
      if (onPhotoChange) {
        onPhotoChange(null, index);
      }
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const handleSaveCaption = async (index) => {
    try {
      await axios.patch(`${API}/photos/${venueId}/caption?owner_id=${ownerId}`, {
        photo_index: index,
        caption: captionText.trim()
      });
      toast.success("Caption saved");
      if (onCaptionChange) {
        onCaptionChange(index, captionText.trim());
      }
      setEditingCaption(null);
      setCaptionText("");
    } catch (error) {
      toast.error("Failed to save caption");
    }
  };

  return (
    <div className="space-y-4">
      {/* Current Photos Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {currentPhotos.map((photo, index) => (
          <motion.div
            key={`photo-${photo}-${index}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative aspect-[4/3] rounded-lg overflow-hidden group"
          >
            <img
              src={photo}
              alt={`Venue photo ${index + 1}`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
            <button
              onClick={() => handleDelete(index)}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
            {index === 0 && (
              <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/50 text-white text-xs rounded">
                Main
              </span>
            )}
            
            {/* Caption area for premium */}
            {isPremium && (
              <div className="absolute bottom-0 left-0 right-0">
                {editingCaption === index ? (
                  <div className="p-2 bg-black/80">
                    <input
                      type="text"
                      value={captionText}
                      onChange={(e) => setCaptionText(e.target.value)}
                      placeholder="Add caption..."
                      className="w-full px-2 py-1 text-xs rounded bg-white/90 text-gray-900"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveCaption(index);
                        if (e.key === 'Escape') setEditingCaption(null);
                      }}
                    />
                    <div className="flex gap-1 mt-1">
                      <button
                        onClick={() => handleSaveCaption(index)}
                        className="flex-1 px-2 py-0.5 bg-brand text-white text-xs rounded"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingCaption(null)}
                        className="px-2 py-0.5 bg-gray-500 text-white text-xs rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingCaption(index);
                      setCaptionText(captions[index] || "");
                    }}
                    className="w-full p-2 bg-black/50 text-white text-xs text-left opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
                  >
                    <Edit2 className="w-3 h-3" />
                    {captions[index] ? captions[index].substring(0, 30) + (captions[index].length > 30 ? '...' : '') : 'Add caption'}
                  </button>
                )}
              </div>
            )}
          </motion.div>
        ))}
        
        {/* Upload Area */}
        {canUpload && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`aspect-[4/3] rounded-lg border-2 border-dashed transition-colors flex flex-col items-center justify-center cursor-pointer ${
              dragOver 
                ? "border-brand bg-brand/5" 
                : "border-gray-200 hover:border-brand hover:bg-gray-50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <Loader2 className="w-8 h-8 text-brand animate-spin" />
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Drop image or click</span>
                <span className="text-xs text-gray-400 mt-1">Max 5MB</span>
              </>
            )}
          </motion.div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Photo Limit Info */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">
          {currentPhotos.length} of {maxPhotos} photos used
        </span>
        {!isPremium && currentPhotos.length >= 2 && (
          <span className="flex items-center gap-1 text-amber-600">
            <Crown className="w-4 h-4" />
            Upgrade for 5 photos
          </span>
        )}
      </div>

      {/* Progress Bar */}
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-brand to-brand-dark transition-all duration-300"
          style={{ width: `${(currentPhotos.length / maxPhotos) * 100}%` }}
        />
      </div>
    </div>
  );
}
